/**
 * dashboardVmBuilder — traduz a resposta de `GET /v1/home` no ViewModel do contrato @v2.
 *
 * Função PURA de propósito: é a peça que decide o que a Home mostra, e precisa ser testável sem
 * montar React. O `useHome` cuida de estado, efeitos e ações.
 *
 * ⚠️ **Este arquivo NÃO decide quais cards existem.** Ele traduz e nada mais. Se aparecer aqui um
 * `filter` por regra de negócio, a decisão D2 foi quebrada — a regra vive no servidor, senão
 * volta a ser impossível ajustar a Home sem deploy do Admin.
 * A única exclusão permitida é a dispensa feita pelo próprio cliente (`dismissed`), que é estado
 * de sessão e não regra de negócio.
 *
 * ⚠️ E ele **NÃO interpreta `data`**. O resumo de cada escopo tem forma própria e quem lê é o
 * desenho daquele card. Traduzir aqui seria trazer regra de negócio de volta para o front.
 */
import { formatRelativeDistance } from '../../utils/formatters';

/** Estado do ESCOPO que o servidor pode anunciar. Não é cor — a cor é decisão da View. */
export const STATUS = ['neutral', 'info', 'success', 'warning', 'error'];

/** O quanto um card pesa agora. Vira largura/posição/destaque na View, não aqui. */
export const IMPORTANCIAS = ['critical', 'high', 'normal', 'low'];

/** Formas de ação que o container sabe executar. */
export const TIPOS_DE_ACAO = ['navigate', 'emit', 'external'];

/**
 * Os escopos que a DashboardView tem desenho PRÓPRIO para renderizar.
 *
 * ⚠️ **Isto é filtro.** Card com `key` fora desta lista é OMITIDO — regra do André (08/09/2026):
 *
 *   *"se não tiver desenho, omite. Todo card que TEM desenho, profissional, bem feito pelo CD,
 *    exibe. Senão não. Nunca quebra."*
 *
 * A alternativa que eu tinha desenhado — um fallback genérico com título e botões — colocaria uma
 * caixa sem cara no meio de uma tela desenhada. Vale menos que não mostrar nada: o cliente perde
 * a informação de um jeito ou de outro, e no fallback ainda leva junto a impressão de produto
 * inacabado.
 *
 * Não é regra de negócio — essa vive no servidor. É a pergunta "eu sei desenhar isto?", que só o
 * front pode responder. O descompasso continua visível onde importa: `useHome` registra no
 * console qual `key` chegou sem desenho.
 *
 * ⚠️ **Handshake com o Claude Design:** desenhou um card novo na DashboardView? A `key` dele entra
 * AQUI no mesmo ciclo. Desenho que existe e não está nesta lista nunca aparece.
 */
export const CHAVES_COM_DESENHO = [
  // operacional do dia
  'appointments',
  'absences',
  'schedules',
  'pages',
  'registry',
  // conta: o que custa dinheiro ou parou de funcionar
  'domainPayment',
  'credits',
  'plan',
  // acompanhamento
  'supportTickets',
  'notifications',
  'finance',
  // venda e conteúdo
  'domainOffer',
  'firstSteps',
  'tips',
  'mobileApp',
  'features',
];

/** @returns {import('./dashboard.vm.js').HomeVm} */
export function montarVm({ home, carregando, errorMessage, refreshing, dispensados, lidoEm }) {
  const state = (() => {
    if (carregando && home === undefined) return 'loading';
    if (errorMessage && home === undefined) return 'error';
    if (!home) return 'error';
    return 'ready';
  })();

  // O servidor já mandou ordenado; reordenar aqui seria assumir uma decisão que não é nossa.
  //
  // Só duas exclusões são permitidas, e nenhuma das duas é regra de negócio:
  //   1. o que o cliente dispensou nesta sessão (preferência dele);
  //   2. o que ainda não tem desenho (só o front sabe responder isso).
  // Qualquer `filter` por regra de negócio aqui quebra a decisão D2.
  const cards = (home?.cards ?? [])
    .filter((c) => CHAVES_COM_DESENHO.includes(c.key))
    .filter((c) => !dispensados.includes(c.key));

  return {
    state,
    errorMessage,
    refreshing,
    lastUpdatedLabel: lidoEm ? `atualizado ${formatRelativeDistance(new Date(lidoEm))}` : '',
    greeting: home?.greeting ?? { title: '', subtitle: '' },
    cards,
    banner: home?.banner ?? null,
    // Conta nova pode legitimamente não ter card nenhum. É estado de tela, não erro — e precisa
    // de tratamento próprio, senão a Home aparece em branco sem explicação.
    isEmpty: state === 'ready' && cards.length === 0,
  };
}
